import React, { useEffect } from "react";
import { Box, Typography, styled } from "@mui/material";
import { useNavigate, Link } from "react-router-dom";

const PageContainer = styled(Box)(({ theme }) => ({
  backgroundColor: "#f4feff",
  minHeight: "100vh",
  width: "100%",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  padding: "40px 20px",
  [theme.breakpoints.down("md")]: {
    padding: "32px 16px",
  },
  [theme.breakpoints.down("sm")]: {
    padding: "16px 8px",
  },
}));

const ContentContainer = styled(Box)(({ theme }) => ({
  maxWidth: "800px",
  width: "100%",
  display: "flex",
  flexDirection: "column",
  gap: "0px",
  boxSizing: "border-box",
  padding: "32px",
  backgroundColor: "#ffffff",
  borderRadius: "16px",
  border: "1px solid #05495a",
  boxShadow: "0px 4px 24px 0px rgba(10,130,157,0.1)",
  [theme.breakpoints.down("md")]: {
    gap: "0px",
    padding: "24px",
  },
  [theme.breakpoints.down("sm")]: {
    gap: "0px",
    padding: "12px",
    borderRadius: "12px",
    maxWidth: "95%",
  },
}));

const BackButton = styled("button")(({ theme }) => ({
  alignSelf: "flex-start",
  background: "none",
  border: "none",
  color: "#0a829d",
  fontSize: "16px",
  cursor: "pointer",
  display: "flex",
  alignItems: "center",
  gap: "8px",
  padding: "8px 0",
  fontFamily: '"Rethink Sans", sans-serif',
  "&:hover": {
    textDecoration: "underline",
  },
  [theme.breakpoints.down("sm")]: {
    fontSize: "14px",
    padding: "6px 0",
  },
}));

const Title = styled(Typography)(({ theme }) => ({
  fontFamily: "Rethink Sans, sans-serif",
  fontWeight: 700,
  fontSize: "28px",
  lineHeight: "35px",
  color: "#033843",
  textAlign: "center",
  marginBottom: "8px",
  [theme.breakpoints.down("md")]: {
    fontSize: "24px",
    lineHeight: "30px",
  },
  [theme.breakpoints.down("sm")]: {
    fontSize: "20px",
    lineHeight: "26px",
    marginBottom: "6px",
  },
}));

const LastUpdated = styled(Typography)(({ theme }) => ({
  fontFamily: "Rethink Sans, sans-serif",
  fontWeight: 400,
  fontSize: "14px",
  color: "#737373",
  textAlign: "center",
  marginBottom: "16px",
  [theme.breakpoints.down("md")]: {
    fontSize: "13px",
    marginBottom: "12px",
  },
  [theme.breakpoints.down("sm")]: {
    fontSize: "12px",
    marginBottom: "10px",
  },
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontFamily: "Rethink Sans, sans-serif",
  fontWeight: 600,
  fontSize: "18px",
  lineHeight: "24px",
  color: "#033843",
  marginBottom: "8px",
  marginTop: "16px",
  [theme.breakpoints.down("md")]: {
    fontSize: "16px",
    lineHeight: "22px",
    marginTop: "14px",
    marginBottom: "6px",
  },
  [theme.breakpoints.down("sm")]: {
    fontSize: "15px",
    lineHeight: "20px",
    marginTop: "12px",
    marginBottom: "6px",
  },
}));

const SectionContent = styled(Typography)(({ theme }) => ({
  fontFamily: "Rethink Sans, sans-serif",
  fontWeight: 400,
  fontSize: "14px",
  lineHeight: "20px",
  color: "#262626",
  marginBottom: "8px",
  wordBreak: "normal",
  overflowWrap: "break-word",
  hyphens: "none",
  whiteSpace: "normal",
  [theme.breakpoints.down("md")]: {
    fontSize: "13px",
    lineHeight: "18px",
    marginBottom: "6px",
  },
  [theme.breakpoints.down("sm")]: {
    fontSize: "12px",
    lineHeight: "16px",
    marginBottom: "6px",
  },
}));

const SubsectionTitle = styled(Typography)(({ theme }) => ({
  fontFamily: "Rethink Sans, sans-serif",
  fontWeight: 600,
  fontSize: "14px",
  lineHeight: "20px",
  color: "#033843",
  marginBottom: "6px",
  marginTop: "10px",
  [theme.breakpoints.down("md")]: {
    fontSize: "13px",
    lineHeight: "18px",
    marginTop: "8px",
    marginBottom: "4px",
  },
  [theme.breakpoints.down("sm")]: {
    fontSize: "12px",
    lineHeight: "16px",
    marginTop: "6px",
    marginBottom: "4px",
  },
}));

const ContactInfo = styled(Box)(({ theme }) => ({
  backgroundColor: "#f4feff",
  border: "1px solid #bfe5ec",
  borderRadius: "8px",
  padding: "20px",
  marginTop: "16px",
  "& p": {
    fontFamily: "Rethink Sans, sans-serif",
    fontSize: "14px",
    lineHeight: "20px",
    color: "#262626",
    margin: "3px 0",
  },
  [theme.breakpoints.down("md")]: {
    padding: "16px",
    marginTop: "14px",
    "& p": {
      fontSize: "13px",
      lineHeight: "18px",
      margin: "2px 0",
    },
  },
  [theme.breakpoints.down("sm")]: {
    padding: "12px",
    marginTop: "12px",
    borderRadius: "6px",
    "& p": {
      fontSize: "12px",
      lineHeight: "16px",
      margin: "2px 0",
    },
  },
}));

const StyledLink = styled("a")(({ theme }) => ({
  color: "#0a829d",
  textDecoration: "none",
  "&:hover": {
    textDecoration: "underline",
  },
}));

const StyledRouterLink = styled(Link)(({ theme }) => ({
  color: "#0a829d",
  textDecoration: "none",
  fontFamily: "Rethink Sans, sans-serif",
  "&:hover": {
    textDecoration: "underline",
  },
}));

export default function BAAPolicy() {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleBack = () => {
    navigate(-1);
  };

  return (
    <PageContainer>
      <ContentContainer>
        <BackButton onClick={handleBack}>← Back</BackButton>

        <Title>
          AffableONE HIPAA Business Associate Agreement (BAA) for UniComply.ai
        </Title>
        <LastUpdated>Effective Date: September 07, 2025</LastUpdated>

        <SectionContent>
          If a Customer is a Covered Entity or Business Associate under the
          Health Insurance Portability and Accountability Act of 1996 ("HIPAA")
          and includes Protected Health Information ("PHI") in data processed
          through the UniComply.ai Platform, this HIPAA Business Associate
          Agreement ("BAA") is incorporated by reference into, and made part of,
          the AffableONE{" "}
          <StyledRouterLink to="/terms-of-service">
            Terms of Service
          </StyledRouterLink>{" "}
          and Data Protection Addendum ("DPA") applicable to the UniComply.ai
          Platform.
        </SectionContent>

        <SectionContent>
          By using the UniComply.ai Platform to store, transmit, or process PHI,
          the Customer agrees that this BAA governs AffableONE's handling of
          PHI as a Business Associate under HIPAA, the HITECH Act, and their
          implementing regulations. This document does not require signature and
          applies automatically to all Covered Entities and Business Associates
          using UniComply.ai for PHI-related purposes.
        </SectionContent>

        <SectionTitle>1. Definitions</SectionTitle>
        <SectionContent>
          Capitalized terms used but not defined in this BAA have the meanings
          assigned under HIPAA, the HITECH Act, or their implementing
          regulations (45 C.F.R. Parts 160 and 164). "AffableONE" refers to
          AffableONE Inc. is headquartered at 1806 E Lake Sammamish Pl SE,
          Sammamish, WA 98075. "Customer" refers to the Covered Entity or
          Business Associate using the UniComply.ai Platform.
        </SectionContent>

        <SectionTitle>
          2. Permitted Uses and Disclosures of Protected Health Information
        </SectionTitle>

        <SubsectionTitle>2.1 Use and Disclosure:</SubsectionTitle>
        <SectionContent>
          AffableONE may use and disclose PHI solely for the purpose of
          providing services through the UniComply.ai Platform and only as
          permitted by this BAA or as required by law.
        </SectionContent>

        <SubsectionTitle>
          2.2 Platform Hosting and Subcontractors:
        </SubsectionTitle>
        <SectionContent>
          UniComply.ai is hosted on Microsoft Azure U.S. data centers. Microsoft
          acts as a downstream Business Associate to AffableONE under its own
          HIPAA Business Associate Agreement. AffableONE ensures that all
          subcontractors handling PHI are bound by written agreements imposing
          the same restrictions and safeguards required under HIPAA.
        </SectionContent>

        <SubsectionTitle>2.3 Safeguards:</SubsectionTitle>
        <SectionContent>
          AffableONE implements reasonable and appropriate administrative,
          technical, and physical safeguards to protect the confidentiality,
          integrity, and availability of PHI in accordance with the HIPAA
          Security Rule (45 C.F.R. Part 164, Subpart C).
        </SectionContent>

        <SubsectionTitle>2.4 Reporting:</SubsectionTitle>
        <SectionContent>
          AffableONE will report to Customer any Breach of Unsecured PHI or
          unauthorized use or disclosure within seventy-two (72) hours of
          discovery. Notification will comply with 45 C.F.R. §164.410(c) and
          include relevant details and mitigation steps.
        </SectionContent>

        <SubsectionTitle>
          2.5 Access, Amendment, and Accounting:
        </SubsectionTitle>
        <SectionContent>
          To the extent AffableONE maintains PHI in a Designated Record Set, it
          will make PHI available to Customer for access, amendment, or
          accounting as required under 45 C.F.R. §§164.524, 164.526, and
          164.528.
        </SectionContent>

        <SubsectionTitle>2.6 Minimum Necessary:</SubsectionTitle>
        <SectionContent>
          AffableONE will request, use, and disclose only the minimum PHI
          necessary to accomplish the intended purpose.
        </SectionContent>

        <SectionTitle>3. Term and Termination</SectionTitle>

        <SubsectionTitle>3.1 Term:</SubsectionTitle>
        <SectionContent>
          This BAA remains in effect as long as Customer uses the UniComply.ai
          Platform for PHI-related purposes and continues thereafter for any PHI
          retained as required by law.
        </SectionContent>

        <SubsectionTitle>3.2 Termination for Cause:</SubsectionTitle>
        <SectionContent>
          AffableONE may terminate access to the Platform if Customer violates
          the Terms of Service or this BAA. Customer may discontinue use if
          AffableONE materially breaches this BAA and fails to cure within ten
          (10) business days after written notice.
        </SectionContent>

        <SubsectionTitle>3.3 Return or Destruction of PHI:</SubsectionTitle>
        <SectionContent>
          Upon termination, AffableONE will return or destroy all PHI where
          feasible. If return or destruction is infeasible, AffableONE will
          extend the protections of this BAA to such PHI and limit its use to
          those purposes that make return or destruction infeasible.
        </SectionContent>

        <SectionTitle>4. Miscellaneous</SectionTitle>

        <SubsectionTitle>4.1 Amendment:</SubsectionTitle>
        <SectionContent>
          This BAA will be automatically updated to reflect any required changes
          under HIPAA, the HITECH Act, or related regulations.
        </SectionContent>

        <SubsectionTitle>4.2 Relationship of Parties:</SubsectionTitle>
        <SectionContent>
          AffableONE acts solely as an independent contractor and Business
          Associate to the Customer. Nothing in this BAA creates an agency or
          employment relationship.
        </SectionContent>

        <SubsectionTitle>4.3 Indemnification:</SubsectionTitle>
        <SectionContent>
          Each Party shall indemnify and hold harmless the other from claims,
          damages, or penalties resulting from its breach of this BAA or HIPAA
          obligations.
        </SectionContent>

        <SubsectionTitle>4.4 Governing Law:</SubsectionTitle>
        <SectionContent>
          This BAA is governed by the laws of the State of Washington. Disputes
          shall be resolved exclusively in the state or federal courts located
          in King County, Washington.
        </SectionContent>

        <SubsectionTitle>4.5 Publication:</SubsectionTitle>
        <SectionContent>
          This document is published by AffableONE Inc. as part of its
          public compliance documentation and is available at{" "}
          <StyledLink
            href="https://unicomply.ai/hipaa-baa"
            target="_blank"
            rel="noopener noreferrer"
          >
            https://unicomply.ai/hipaa-baa
          </StyledLink>
          . No signature is required for enforceability; it is effective
          automatically upon a Customer's use of the UniComply.ai Platform for
          PHI-related purposes.
        </SectionContent>

        <SectionTitle>Contact and Notices</SectionTitle>
        <SectionContent>
          For legal or compliance notices under this BAA, please contact:
        </SectionContent>
        <ContactInfo>
          <Typography component="p">
            <strong>AffableONE Inc.</strong>
          </Typography>
          <Typography component="p">1806 E Lake Sammamish Pl SE</Typography>
          <Typography component="p">Sammamish, WA 98075</Typography>
          <Typography component="p">
            Email:{" "}
            <StyledLink href="mailto:support@affableone.ai">
              support@affableone.ai
            </StyledLink>
          </Typography>
        </ContactInfo>

        <SectionContent sx={{ marginTop: "24px", fontStyle: "italic" }}>
          AffableONE Inc. — UniComply.ai. Published September 07, 2025.
          This page provides the standard HIPAA Business Associate Agreement
          applicable to Customers using UniComply.ai to process PHI. Customers
          requiring an executed (signed) BAA for their records may contact
          legal@affableone.ai.
        </SectionContent>
      </ContentContainer>
    </PageContainer>
  );
}
